// Phase 3B — relationship-driven "what to experience next" resolver.
//
// Deterministic and explainable. Every suggestion carries a provenance so the
// UI can state *why* it is being offered, and nothing is invented: sources are
// the authored ecosystem graph (ecosystem.ts), the 47 Gates knowledge graph
// (knowledge.ts), and the experiential sequence below.

import { ECOSYSTEM, findItem, getRelated, type EcosystemItem, type ItemKind } from "./ecosystem";
import { gatesForItem } from "./knowledge";
import type { MemoryKind, PathMemoryEntry, ReturnState } from "./return-loop";

export type RecoProvenance = "authored-relationship" | "knowledge-graph" | "sequence" | "editorial";

export interface NextExperience {
  kind: MemoryKind;
  ref: string;
  title: string;
  href: string;
  /** Plain-language justification shown to the visitor. */
  reason: string;
  provenance: RecoProvenance;
}

/**
 * The experiential sequence. A first experience should be felt (hear), then
 * seen/read, then practised — depth is offered only after the loop closes.
 */
export const SEQUENCE: MemoryKind[] = ["music", "wisdom", "activation", "gate", "book", "academy"];

function nextKindAfter(kind: MemoryKind): MemoryKind {
  const i = SEQUENCE.indexOf(kind);
  return SEQUENCE[(i + 1) % SEQUENCE.length]!;
}

function toNext(item: EcosystemItem, reason: string, provenance: RecoProvenance): NextExperience {
  return {
    kind: item.kind as MemoryKind,
    ref: item.slug,
    title: item.title,
    href: item.href,
    reason,
    provenance,
  };
}

function firstOfKind(kind: ItemKind, exclude: Set<string>): EcosystemItem | undefined {
  return ECOSYSTEM.find((i) => i.kind === kind && !exclude.has(`${i.kind}:${i.slug}`));
}

export interface RecommendInput {
  memory: PathMemoryEntry[];
  state: ReturnState;
  limit?: number;
}

/**
 * Resolves the next experiences for a visitor from what they have actually
 * done. Falls back to editorial defaults for a first visit — never to random
 * content and never to an empty list.
 */
export function recommendNext(input: RecommendInput): NextExperience[] {
  const limit = input.limit ?? 3;
  const seen = new Set(input.memory.map((m) => `${m.kind}:${m.ref}`));
  const out: NextExperience[] = [];
  const push = (n: NextExperience | undefined | null) => {
    if (!n) return;
    if (seen.has(`${n.kind}:${n.ref}`)) return;
    if (out.some((o) => o.kind === n.kind && o.ref === n.ref)) return;
    out.push(n);
  };

  const anchor = [...input.memory].sort((a, b) => b.at - a.at)[0];

  if (anchor && anchor.kind !== "gate" && anchor.kind !== "academy") {
    const item = findItem(anchor.kind as ItemKind, anchor.ref);
    if (item) {
      // 1. Authored / derived relationships from the ecosystem graph.
      for (const rel of getRelated(item.kind, item.slug, 4)) {
        push(toNext(rel, `Connected to ${item.title}.`, "authored-relationship"));
      }
      // 2. The Gate this experience resonates with.
      const gate = gatesForItem(item, 1)[0];
      if (gate) {
        push({
          kind: "gate",
          ref: String(gate.n),
          title: `Gate ${gate.n} · ${gate.title}`,
          href: `/gates/${gate.n}`,
          reason: `The Gate beneath ${item.title}.`,
          provenance: "knowledge-graph",
        });
      }
      // 3. The next form in the experiential sequence.
      const nk = nextKindAfter(anchor.kind);
      if (nk !== "gate" && nk !== "academy") {
        const seq = firstOfKind(nk as ItemKind, seen);
        if (seq) push(toNext(seq, "The next form of the same teaching.", "sequence"));
      }
    }
  }

  // 4. Editorial floor — always something real to begin with.
  if (out.length < limit) {
    for (const kind of ["music", "wisdom", "activation"] as ItemKind[]) {
      const item = firstOfKind(kind, seen);
      if (item) push(toNext(item, "A grounded place to begin.", "editorial"));
      if (out.length >= limit) break;
    }
  }

  return out.slice(0, limit);
}
